import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { automationApi, type AutomationTermList } from '@/api/automationApi';
import { useToast } from '@/components/ui/Toast';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Table, TableWrap, TableHeader, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { cn } from '@/utils/cn';

function ListEditor({
  profileId,
  initial,
  onClose,
}: {
  profileId: string;
  initial?: AutomationTermList | null;
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(initial?.name ?? '');
  const [kind, setKind] = useState<'blacklist' | 'whitelist'>(initial?.kind ?? 'blacklist');
  const [match, setMatch] = useState<'exact' | 'phrase_contains'>(initial?.match ?? 'exact');
  const [entriesText, setEntriesText] = useState((initial?.entries ?? []).join('\n'));
  const [autoApply, setAutoApply] = useState(Boolean(initial?.autoApply));
  const [scheduleHour, setScheduleHour] = useState(initial?.scheduleHour ?? 9);
  const [status, setStatus] = useState(initial?.status ?? 'active');

  const saveMut = useMutation({
    mutationFn: async () => {
      const entries = entriesText
        .split(/[\n,]+/)
        .map((t) => t.trim())
        .filter(Boolean);
      const body = {
        name,
        kind,
        match,
        entries,
        autoApply: kind === 'blacklist' ? autoApply : false,
        scheduleHour,
        status,
        entityScope: 'all',
      };
      if (initial?.id) {
        return automationApi.updateTermList(profileId, initial.id, body);
      }
      return automationApi.createTermList(profileId, body);
    },
    onSuccess: () => {
      showToast(initial?.id ? 'List updated' : 'List created', 'success');
      void qc.invalidateQueries({ queryKey: ['automation-term-lists', profileId] });
      onClose();
    },
    onError: (e: unknown) => {
      showToast(e instanceof Error ? e.message : 'Save failed', 'error');
    },
  });

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10">
      <div className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            {initial ? 'Edit list' : 'Create list'}
          </h2>
          <button type="button" className="text-foreground-muted hover:text-foreground" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="space-y-3 p-5">
          <label className="block text-sm">
            <span className="mb-1 block text-foreground-muted">Name</span>
            <input
              className="w-full rounded border border-border px-3 py-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="flex gap-2">
            {(['blacklist', 'whitelist'] as const).map((k) => (
              <button
                key={k}
                type="button"
                className={cn(
                  'rounded border px-3 py-1 text-sm capitalize',
                  kind === k ? 'border-brand bg-brand/10 text-brand' : 'border-border'
                )}
                onClick={() => setKind(k)}
              >
                {k}
              </button>
            ))}
          </div>
          <label className="block text-sm">
            <span className="mb-1 block text-foreground-muted">Match</span>
            <select
              className="w-full rounded border border-border px-3 py-2"
              value={match}
              onChange={(e) => setMatch(e.target.value as 'exact' | 'phrase_contains')}
            >
              <option value="exact">Exact</option>
              <option value="phrase_contains">Phrase contains</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-foreground-muted">Terms (one per line)</span>
            <textarea
              className="min-h-[120px] w-full rounded border border-border px-3 py-2"
              value={entriesText}
              onChange={(e) => setEntriesText(e.target.value)}
            />
          </label>
          {kind === 'blacklist' && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoApply}
                onChange={(e) => setAutoApply(e.target.checked)}
              />
              Auto-apply as negative exact daily
            </label>
          )}
          {kind === 'blacklist' && autoApply && (
            <label className="block text-sm">
              <span className="mb-1 block text-foreground-muted">Hour</span>
              <input
                type="number"
                min={0}
                max={23}
                className="w-full rounded border border-border px-3 py-2"
                value={scheduleHour}
                onChange={(e) => setScheduleHour(Number(e.target.value))}
              />
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={status === 'active'}
              onChange={(e) => setStatus(e.target.checked ? 'active' : 'paused')}
            />
            Active
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || saveMut.isPending} onClick={() => saveMut.mutate()}>
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TermListsSection({
  profileId,
  canWrite,
  createOpen,
  onCreateOpenChange,
}: {
  profileId: string;
  canWrite: boolean;
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
}) {
  const { showToast } = useToast();
  const qc = useQueryClient();
  const [edit, setEdit] = useState<AutomationTermList | null>(null);
  const open = createOpen;
  const listsQ = useQuery({
    queryKey: ['automation-term-lists', profileId],
    enabled: Boolean(profileId),
    queryFn: () => automationApi.listTermLists(profileId).then((r) => r.data),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => automationApi.deleteTermList(profileId, id),
    onSuccess: () => {
      showToast('List deleted', 'success');
      void qc.invalidateQueries({ queryKey: ['automation-term-lists', profileId] });
    },
    onError: (e: unknown) => {
      showToast(e instanceof Error ? e.message : 'Delete failed', 'error');
    },
  });

  return (
    <>
      {listsQ.isLoading ? (
        <Spinner />
      ) : (
        <TableWrap>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Match</TableHead>
                <TableHead>Terms</TableHead>
                <TableHead>Auto-apply</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <tbody>
              {(listsQ.data?.items ?? []).map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell className="capitalize">{row.kind}</TableCell>
                  <TableCell>{row.match === 'phrase_contains' ? 'Phrase contains' : 'Exact'}</TableCell>
                  <TableCell>{row.entries?.length ?? 0}</TableCell>
                  <TableCell>{row.autoApply ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {canWrite && (
                        <>
                          <button
                            type="button"
                            className="text-sm text-brand"
                            onClick={() => {
                              setEdit(row);
                              onCreateOpenChange(true);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-sm text-foreground-muted"
                            onClick={() => delMut.mutate(row.id)}
                          >
                            <Trash2 className="inline h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {(listsQ.data?.items ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-foreground-muted">
                    No lists yet.{' '}
                    {canWrite && (
                      <button type="button" className="text-brand" onClick={() => onCreateOpenChange(true)}>
                        Create one
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </tbody>
          </Table>
        </TableWrap>
      )}
      {open && (
        <ListEditor
          profileId={profileId}
          initial={edit}
          onClose={() => {
            onCreateOpenChange(false);
            setEdit(null);
          }}
        />
      )}
    </>
  );
}

export function TermListsCreateButton({
  onClick,
}: {
  onClick: () => void;
}) {
  return (
    <Button onClick={onClick}>
      <Plus className="mr-1 h-4 w-4" />
      Create list
    </Button>
  );
}
